/**
 * Composant pour afficher les états d'erreur
 * Avec bouton de retry et messages clairs
 */
import { Link } from 'react-router-dom'

interface ErrorStateProps {
  title?: string
  message: string
  onRetry?: () => void
  retryLabel?: string
  variant?: 'error' | 'warning' | 'info'
}

const VARIANT_STYLES = {
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-500',
    title: 'text-red-800',
    message: 'text-red-700',
    button: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: 'text-amber-500',
    title: 'text-amber-800',
    message: 'text-amber-700',
    button: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    icon: 'text-blue-500',
    title: 'text-blue-800',
    message: 'text-blue-700',
    button: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
  },
}

export default function ErrorState({
  title = 'Une erreur est survenue',
  message,
  onRetry,
  retryLabel = 'Réessayer',
  variant = 'error',
}: ErrorStateProps) {
  const styles = VARIANT_STYLES[variant]

  return (
    <div
      className={`
        ${styles.bg} ${styles.border} border rounded-lg p-6
        flex flex-col items-center text-center
      `}
      role="alert"
    >
      {/* Icône */}
      <div className={`${styles.icon} mb-4`}>
        {variant === 'error' && (
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        )}
        {variant === 'warning' && (
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        )}
        {variant === 'info' && (
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        )}
      </div>

      {/* Titre */}
      <h3 className={`text-lg font-semibold ${styles.title} mb-2`}>{title}</h3>

      {/* Message */}
      <p className={`${styles.message} mb-4 max-w-md`}>{message}</p>

      {/* Bouton retry */}
      {onRetry && (
        <button
          onClick={onRetry}
          className={`
            inline-flex items-center gap-2 px-4 py-2
            text-white font-medium rounded-lg
            transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2
            ${styles.button}
          `}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          {retryLabel}
        </button>
      )}
    </div>
  )
}

/**
 * Erreur 404 spécifique pour substance non trouvée
 */
export function SubstanceNotFound({ cas, onRetry }: { cas: string; onRetry?: () => void }) {
  return (
    <ErrorState
      title="Substance non trouvée"
      message={`Aucune donnée disponible pour le numéro CAS "${cas}". Vérifiez le format (ex: 71-43-2) ou essayez une autre substance.`}
      onRetry={onRetry}
      variant="warning"
    />
  )
}

/**
 * Erreur réseau
 */
export function NetworkError({ onRetry }: { onRetry?: () => void }) {
  return (
    <ErrorState
      title="Erreur de connexion"
      message="Impossible de contacter le serveur. Vérifiez votre connexion internet et réessayez."
      onRetry={onRetry}
      variant="error"
    />
  )
}

/**
 * Erreur quota atteint (paywall)
 */
export function QuotaExceeded({ onUpgrade }: { onUpgrade?: () => void }) {
  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-8 text-center">
      <div className="text-purple-500 mb-4">
        <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
      </div>

      <h3 className="text-xl font-bold text-gray-900 mb-2">
        Quota de recherches atteint
      </h3>
      <p className="text-gray-600 mb-6 max-w-md mx-auto">
        Vous avez utilisé vos 3 recherches gratuites. Passez à l'abonnement professionnel
        pour un accès illimité aux fiches toxicologiques.
      </p>

      {onUpgrade && (
        <button
          onClick={onUpgrade}
          className="
            inline-flex items-center gap-2 px-6 py-3
            bg-gradient-to-r from-purple-600 to-blue-600
            text-white font-semibold rounded-lg
            hover:from-purple-700 hover:to-blue-700
            transition-all shadow-lg hover:shadow-xl
            focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
          "
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
          Passer à l'abonnement
        </button>
      )}

      <p className="mt-4 text-sm text-gray-500">
        À partir de 19€/mois • Annulation à tout moment
      </p>

      {/* Lien retour accueil */}
      <div className="mt-6 pt-4 border-t border-purple-100">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Retour à l'accueil
        </Link>
      </div>
    </div>
  )
}
